/**
 * The picker debug panel's country preview — client half.
 *
 * A previewed country has to survive the panel being closed and the page being reloaded (checkout
 * bounces through Stripe and back), so it lives in localStorage rather than component state. It is a
 * *preview key*, not a permission: the server honors `?country=` for a Wix employee only, so hand-
 * writing this key in a real user's browser changes nothing they see.
 *
 * The key is named for the product, not the `orion-web:` legacy prefix — see the orion-web CLAUDE.md
 * rule: a NEW persisted key has no migration cost and must not inherit it.
 */

const KEY = 'symphony-picker-country-preview';

function isCountryCode(code: string): boolean {
  return /^[A-Z]{2}$/.test(code);
}

/** The previewed country, or "" when the picker should use the caller's real signals. */
export function readCountryPreview(): string {
  if (typeof window === 'undefined') return '';
  try {
    const code = (window.localStorage.getItem(KEY) ?? '').trim().toUpperCase();
    return isCountryCode(code) ? code : '';
  } catch {
    return ''; // storage blocked (private mode / disabled cookies) → no preview, never a throw
  }
}

/** Store a previewed country; "" clears it. Invalid input clears rather than persisting garbage. */
export function writeCountryPreview(code: string): void {
  if (typeof window === 'undefined') return;
  const next = code.trim().toUpperCase();
  try {
    if (isCountryCode(next)) window.localStorage.setItem(KEY, next);
    else window.localStorage.removeItem(KEY);
  } catch {
    // Storage blocked — the preview just doesn't persist. Nothing to report in dev chrome.
  }
}

/**
 * Append the preview to a picker API url. No preview → the url is returned untouched, so a normal
 * user's requests are byte-identical to what they were before this existed (same cache key included).
 */
export function withCountryPreview(url: string, country: string): string {
  if (!country) return url;
  return `${url}${url.includes('?') ? '&' : '?'}country=${encodeURIComponent(country)}`;
}

/**
 * "Germany (DE)" for the dropdown. `Intl.DisplayNames` ships with every browser we support; a locale
 * it can't name degrades to the bare code rather than throwing (debug chrome is English-only, so the
 * names are deliberately not localized).
 */
export function countryLabel(code: string): string {
  try {
    const name = new Intl.DisplayNames(['en'], { type: 'region' }).of(code);
    return name && name !== code ? `${name} (${code})` : code;
  } catch {
    return code;
  }
}
